import React, { useState, useEffect } from 'react';
import { useLocation } from '@reach/router';
import { parse } from 'query-string';

import { Form, Field } from 'react-final-form';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import TextInput from '~components/TextInput';
import Checkbox from '~components/Checkbox';
import Select from '~components/Select';
import { email, name, cardElement, promoCode } from '~utils/validations';
import fields from './constants';
import { text, usaZipCode } from '~utils/validations';
import clsx from 'clsx';
import { MenuItem, CircularProgress } from '@material-ui/core';

import { orderTypes, countriesToRegionsMap } from './constants';
import { Error } from '../Error/Error';
import { subscribe } from '../../services/checkout';
import { OnChange } from 'react-final-form-listeners';

import { useAnalytics } from '~utils/useAnalytics';
import { getPromoNumberOfMonthes } from '~utils/promoCode';

import styles from './styles.module.scss';

const OrderForm = ({
  onComplete,
  type, // Accepts "us" | "international"
  purchaseSources,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState();
  const {
    analyticsLoaded,
    trackRegisterPageViewed,
    trackRegisterPageSubmitTapped,
    trackRegisterSuccessfulPageViewed,
    trackRegisterPaymentFailure,
    saveFormData,
  } = useAnalytics();

  const stripeLoaded = stripe && elements;

  const location = useLocation();
  const searchParams = parse(location.search);

  const getFieldInitialValue = (field) => {
    return searchParams[field];
  };

  useEffect(() => {
    const promoCode = getFieldInitialValue(fields.subscriptionCreditCode);
    const orderId = getFieldInitialValue(fields.orderId);
    analyticsLoaded && trackRegisterPageViewed(promoCode, orderId);
  }, [analyticsLoaded]);

  async function handleSubmit(formData) {
    if (!stripeLoaded) {
      return;
    }

    saveFormData('register_form', formData);

    const cardElement = elements.getElement(CardElement);

    const subscription = makeSubscription(formData);
    const stripeData = makeStripeData(formData);

    try {
      const { error, token } = await stripe.createToken(cardElement, stripeData);

      if (error) {
        setError(error.message);
        trackRegisterPageSubmitTapped(formData);
        trackRegisterPaymentFailure(formData, error.message);
        return;
      }

      subscription.stripe_token = token.id;

      await subscribe(subscription);

      trackRegisterSuccessfulPageViewed(formData);
      if (formData[fields.subscriptionCreditCode]) {
        trackRegisterPageSubmitTapped(formData, { isPromoGiftCodeSuccessful: true });
        saveFormData('register_form', { ...formData, is_gift_code_valid: true });
        const numberOfGiftedMonths = getPromoNumberOfMonthes(formData[fields.subscriptionCreditCode]);
        onComplete && onComplete(false, numberOfGiftedMonths);
      } else {
        trackRegisterPageSubmitTapped(formData);
        saveFormData('register_form', formData);
        onComplete && onComplete();
      }
    } catch (err) {
      // TODO: logError(err);
      const { detail, status } = err?.response?.data?.errors[0] || {};
      if (status === 401) {
        trackRegisterPageSubmitTapped(formData);
        onComplete && onComplete(true);
      } else if (detail) {
        setError(detail);
        if (detail?.includes('Sorry there was an issue with your gift code')) {
          trackRegisterPageSubmitTapped(formData, { isPromoGiftCodeSuccessful: false });
        } else {
          const paymentErrors = [
            "Your card's security code is incorrect.",
            'Your card was declined.',
            'Your card has insufficient funds.',
            'Your card has expired.',
            'An error occurred while processing your card. Try again in a little bit.',
          ];
          if (paymentErrors.includes(detail)) {
            trackRegisterPaymentFailure(formData, detail);
          }
          trackRegisterPageSubmitTapped(formData);
        }
      } else {
        setError(err);
        trackRegisterPageSubmitTapped(formData);
      }
    }
  }

  const getRegionLabel = (values) => {
    if (values) {
      if (isUS(values)) {
        return 'State';
      } else if (isUAE(values)) {
        return 'Emirate';
      } else return 'Region';
    } else {
      return 'Region';
    }
  };

  const isUS = (values) => {
    return values[fields.billingAddress]?.country === 'United States';
  };

  const isUAE = (values) => {
    return values[fields.billingAddress]?.country === 'United Arab Emirates';
  };
  return (
    <Form
      onSubmit={(formData) => handleSubmit(formData)}
      validate={() => true}
      keepDirtyOnReinitialize
      render={({ handleSubmit, submitting, invalid, setFieldValue, values, form }) => {
        return (
          <div className={styles.container}>
            <form className={styles.formContainer} onSubmit={handleSubmit} noValidate>
              <div className={styles.generalInfo}>
                <h3 className={styles.title}>
                  Unlock your
                  <br />
                  Tempo membership.
                </h3>
                <p>Lift smarter, train more efficiently, and easily track your progress.</p>
                <Field
                  className={styles.field}
                  name={fields.firstName}
                  component={TextInput}
                  label="First Name"
                  validate={name}
                  required
                  initialValue={getFieldInitialValue(fields.firstName)}
                />
                <Field
                  className={styles.field}
                  name={fields.lastName}
                  component={TextInput}
                  label="Last Name"
                  validate={name}
                  required
                  initialValue={getFieldInitialValue(fields.lastName)}
                />
                <Field
                  className={styles.field}
                  name={fields.email}
                  component={TextInput}
                  label="Email Address"
                  type="email"
                  validate={email}
                  required
                  initialValue={getFieldInitialValue(fields.email)}
                />
                <div className={clsx(styles.row, styles.field)}>
                  <Field
                    component={Select}
                    extraClassName="muiFormControl"
                    name={fields.purchaseSource}
                    label="Purchased From"
                    validate={text}
                    required
                    defaultValue={purchaseSources.find((s) => s.default)?.value}
                  >
                    {purchaseSources.map((source) => (
                      <MenuItem key={source.value} value={source.value}>
                        {source.name}
                      </MenuItem>
                    ))}
                  </Field>
                  {type === 'us' && values[fields.purchaseSource] === 'tempo' && (
                    <Field
                      className={clsx(styles.field, styles.orderId)}
                      name={fields.orderId}
                      component={TextInput}
                      label="Order Number"
                      validate={text}
                      required={values[fields.purchaseSource] === 'tempo'}
                      initialValue={getFieldInitialValue(fields.orderId)}
                    />
                  )}
                  <OnChange name={fields.purchaseSource}>
                    {(value) => {
                      if (value === 'tempo') {
                        form.change(fields.orderType, undefined);
                      } else {
                        form.change(fields.orderId, undefined);
                      }
                    }}
                  </OnChange>
                </div>
                {values[fields.purchaseSource] !== 'tempo' && (
                  <div className={clsx(styles.row, styles.field)}>
                    <Field
                      component={Select}
                      extraClassName="muiFormControl"
                      name={fields.orderType}
                      label={values[fields.purchaseSource] == 'gift' ? 'What was your gift?' : 'What did you purchase?'}
                      validate={text}
                      required
                    >
                      {orderTypes.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.name}
                        </MenuItem>
                      ))}
                    </Field>
                  </div>
                )}

                <h4 className={styles.subtitle}>Have a code to redeem?</h4>

                <Field
                  className={`${styles.field}`}
                  name={fields.subscriptionCreditCode}
                  component={TextInput}
                  label="Promo or Gift Code"
                  initialValue={getFieldInitialValue(fields.subscriptionCreditCode)}
                  validate={promoCode}
                >
                  {({ input, meta }) => {
                    let giftedMonths = 0;
                    if (meta.valid && input?.value) {
                      let matches = input.value.match(/(03|06|12|24)/);
                      if (matches?.length) {
                        giftedMonths = parseInt(matches[0]);
                      }
                    }
                    return (
                      <>
                        <TextInput
                          input={input}
                          meta={meta}
                          label="Promo or Gift Code"
                          className={clsx({
                            [styles.promoCode__valid]: meta.valid && input?.value,
                            [styles.promoCode__invalid]: !meta.valid,
                          })}
                        />
                        {giftedMonths > 0 && <p class={styles.giftMessage}>Code applied to your membership.</p>}
                      </>
                    );
                  }}
                </Field>
              </div>

              <h3 className={styles.title}>Payment info</h3>
              <p>
                Membership begins when you receive and activate your Tempo. If you’ve redeemed a code, you’ll be charged
                when it expires.
              </p>
              <Field className={styles.field} name={fields.stripeToken} validate={cardElement} required>
                {({ input }) => (
                  <CardElement
                    id="card-element"
                    className={styles.cardElement}
                    options={{
                      style: {
                        base: {
                          color: `#000`,
                          fontFamily: `${styles.fontFamily}`,
                          fontSize: '18px',
                          fontSmoothing: 'antialiased',
                          ':-webkit-autofill': {
                            backgroundColor: '#FFF',
                          },
                          '::placeholder': {
                            color: `${styles.baseGray}`,
                            textTransform: 'capitalize',
                          },
                        },
                        invalid: {
                          color: `${styles.brandRed}`,
                        },
                      },
                      hidePostalCode: true,
                    }}
                    onChange={input.onChange}
                    onFocus={input.onFocus}
                    onBlur={input.onBlur}
                  />
                )}
              </Field>
              <Field
                className={styles.field}
                component={TextInput}
                name={fields[`billingLine1`]}
                label="Street Address"
                validate={text}
                fullWidth
                required
                initialValue={getFieldInitialValue(fields[`billingLine1`])}
              />
              <Field
                className={styles.field}
                component={TextInput}
                name={fields[`billingLine2`]}
                label="Apt/Floor/Suite"
                fullWidth
                initialValue={getFieldInitialValue(fields[`billingLine2`])}
              />
              <Field
                className={styles.field}
                component={TextInput}
                name={fields[`billingCity`]}
                label="City"
                validate={text}
                required
                initialValue={getFieldInitialValue(fields[`billingCity`])}
              />

              <div className={clsx(styles.row)}>
                <Field
                  className={clsx(styles.field, styles.country)}
                  component={Select}
                  extraClassName="muiFormControl"
                  name={fields[`billingCountry`]}
                  label="Country"
                  validate={text}
                  required
                  defaultValue={type === 'us' ? 'United States' : 'United Arab Emirates'}
                >
                  {Array.from(countriesToRegionsMap.keys()).map((country) => (
                    <MenuItem key={country} value={country}>
                      {country}
                    </MenuItem>
                  ))}
                </Field>

                <OnChange name={fields[`billingCountry`]}>
                  {(value) => {
                    const address = values[fields.billingAddress];
                    if (value === 'United Arab Emirates') {
                      const addressWithTempPostalCode = { ...address, postal_code: '00000' };
                      form.change(fields.billingAddress, addressWithTempPostalCode);
                    } else {
                      if (address?.postal_code === '00000') {
                        const addressWithoutTempPostalcode = { ...address, postal_code: undefined };
                        form.change(fields.billingAddress, addressWithoutTempPostalcode);
                      }
                    }
                  }}
                </OnChange>

                <Field
                  className={clsx(styles.field)}
                  component={Select}
                  extraClassName="muiFormControl"
                  name={fields[`billingProvince`]}
                  label={getRegionLabel(values)}
                  validate={text}
                  required={countriesToRegionsMap.get(values[fields.billingAddress]?.country)?.length}
                  disabled={!values[fields.billingAddress]?.country}
                >
                  {(countriesToRegionsMap.get(values[fields.billingAddress]?.country) || []).map((region) => (
                    <MenuItem key={region} value={region}>
                      {region}
                    </MenuItem>
                  ))}
                </Field>
              </div>

              <Field
                className={clsx(styles.field, isUAE(values) ? styles.hidden : '')}
                component={TextInput}
                name={fields[`billingPostalCode`]}
                label={isUS(values) ? 'Zip Code' : 'Postal Code'}
                validate={isUS(values) ? usaZipCode : null}
                required={isUS(values)}
                initialValue={
                  type === 'us' || !isUAE(values) ? getFieldInitialValue(fields[`billingPostalCode`]) : '00000'
                }
              />

              {error && <Error error={error} className={styles.row} />}
              <div className={styles.termsOfService}>
                <Field
                  required
                  component={Checkbox}
                  name={fields[`hasAcceptedTerms`]}
                  type="checkbox"
                  className={styles.row}
                  onChange={() => setFieldValue('has_accepted_terms', !values.has_accepted_terms)}
                  label={
                    <>
                      I have read and accept{' '}
                      <a href="https://tempo.fit/terms-of-service" target="_blank" rel="noopener noreferrer">
                        Tempo's Terms of Service
                      </a>
                    </>
                  }
                />
              </div>
              <button
                className={styles.button}
                type="submit"
                disabled={submitting || invalid || !stripeLoaded || values.has_accepted_terms !== true}
              >
                Submit {submitting && <CircularProgress className={styles.spinner} color="inherit" size={24} />}
              </button>
            </form>
          </div>
        );
      }}
    />
  );
};

OrderForm.displayName = 'OrderForm';

const makeSubscription = (formData) => {
  const subscription = { ...formData,  
    ...(formData["order_type"] ? 
      { order_type: formData["order_type"] === "core" ?
       "move" 
      : formData["order_type"] 
    } : {})
  };
  return subscription;
};

const makeStripeData = (formData) => {
  const address = formData.stripe_billing_address;
  return {
    name: `${formData.first_name} ${formData.last_name}`,
    address_line1: address.line1,
    address_line2: address.line2,
    address_city: address.city,
    address_province: address.province,
    address_zip: address.postal_code,
    address_country: address.country,
  };
};

export default OrderForm;
